import { Plug, Sparkles } from "lucide-react";

/**
 * Tier 1 venue notice - shown on Courts / Bookings tabs (and anywhere
 * the manual-entry UX is hidden because direct sync handles it).
 * Mirrors the messaging in the venue_approved Tier 1 email:
 *
 *   * Courts and availability are pulled from the venue's booking
 *     system (MINDBODY, Club Automation, Bookteq, etc.) once direct
 *     sync ships for them
 *   * Until then, nothing for them to do here
 *   * Stripe + Partner Agreement are still on them
 */
export function ExternalSystemNotice({
  systemName,
  variant,
}: {
  systemName: string;
  /** Determines the headline + body copy. "courts" hides the
   *  Courts-tab manual-entry UX; "availability" hides the
   *  Bookings-tab calendar editor. */
  variant: "courts" | "availability";
}) {
  const heading =
    variant === "courts"
      ? `Courts sync from ${systemName}`
      : `Availability syncs from ${systemName}`;
  const body =
    variant === "courts" ? (
      <>
        Once direct sync is live, your courts flow into Whistll
        automatically - every {systemName} resource you tag as a court is
        listed here. <span className="text-foreground">Nothing for you to
        do here in the meantime.</span> When sync ships, we'll walk you
        through a one-time ~2-min setup (tick which {systemName} resources
        are courts + set the per-player Whistll price for each) and
        you're done.
      </>
    ) : (
      <>
        Manage your schedule in {systemName} as you always have. Once
        direct sync is live, your availability flows into Whistll
        automatically - every change you make in {systemName} updates
        Whistll within minutes, and Whistll bookings write back into your
        {" "}{systemName} calendar so you never get double-booked.
      </>
    );

  return (
    <div className="rounded-2xl border border-volt/30 bg-volt-subtle p-6">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-volt/15 text-volt">
          <Plug className="h-4 w-4" />
        </span>
        <span className="text-xs font-bold uppercase tracking-wider text-volt">
          Direct sync · {systemName}
        </span>
      </div>
      <h2 className="mb-2 text-xl font-semibold tracking-tight">
        {heading}
      </h2>
      <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
        {body}
      </p>
      <p className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground">
        <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-volt" />
        <span>
          We'll let you know the moment {systemName} direct sync goes live
          for your venue. Until then, focus on Stripe + the partner
          agreement - those are independent of the booking-system side.
        </span>
      </p>
    </div>
  );
}

/**
 * Tier 1 venues' booking_system field stores whatever the applicant
 * picked in the form (e.g. "MINDBODY", "CLUB AUTOMATION", "Bookteq").
 * Tier 2 is the special sentinel "__none__" set when "Use Whistll
 * dashboard" is picked. Treat empty/null/sentinel as Tier 2.
 */
export function isExternalBookingSystem(
  bookingSystem: string | null | undefined,
): boolean {
  const v = (bookingSystem ?? "").trim();
  return v !== "" && v !== "__none__";
}
